import type { NavigateFunction } from 'react-router-dom'
import {
  Notification,
  NotificationCategory,
} from '@dataTypes/notification.types'

/**
 * Legacy keyword heuristic that buckets a notification into one of the eight
 * module-aligned categories. Only used as a fallback for mock-data items that
 * don't carry a backend-supplied `category`; server-hydrated notifications
 * should rely on `notification.category` directly.
 */
export const getNotificationCategory = (
  notification: Notification,
): NotificationCategory => {
  const haystack = `${notification.title} ${notification.message}`.toLowerCase()
  const has = (...keywords: string[]): boolean =>
    keywords.some((k) => haystack.includes(k))

  if (has('claim', 'denial', 'insurance', 'eob', 'payer')) {
    return NotificationCategory.CLAIMS
  }
  if (has('payment', 'invoice', 'card declined', 'billing', 'payment plan')) {
    return NotificationCategory.BILLING
  }
  if (
    has(
      'appointment',
      'check-in',
      'check in',
      'scheduling',
      'overlap',
      'double booking',
      'waitlist',
    )
  ) {
    return NotificationCategory.APPOINTMENTS
  }
  if (has('form')) {
    return NotificationCategory.FORMS
  }
  if (has('chart', 'treatment', 'perio', 'condition')) {
    return NotificationCategory.CHARTING
  }
  if (has('note', 'audit trail')) {
    return NotificationCategory.NOTES
  }
  if (has('location', 'clinic', 'network', 'system')) {
    return NotificationCategory.CLINICS
  }
  if (
    has('message', 'sms', 'email', 'call', 'communication', 'chat', 'opt-out')
  ) {
    return NotificationCategory.MESSAGE_CENTER
  }

  // Default catch-all: items without an obvious module bucket land under
  // Clinics (the closest equivalent of the legacy "Administrative" bucket).
  return NotificationCategory.CLINICS
}

/** Same-origin relative paths only (e.g. `/claims`). Rejects absolute and protocol-relative URLs. */
export const isSafeInternalActionPath = (actionUrl: string): boolean => {
  const url = actionUrl.trim()
  if (!url.startsWith('/') || url.startsWith('//') || url.includes('\\')) {
    return false
  }
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.origin === window.location.origin
  } catch {
    return false
  }
}

export const navigateToNotificationTarget = (
  navigate: NavigateFunction,
  notification: Notification,
): boolean => {
  if (
    notification.action_url &&
    isSafeInternalActionPath(notification.action_url)
  ) {
    const meta = notification.action_metadata
    navigate(notification.action_url, meta ? { state: meta } : undefined)
    return true
  }
  if (
    notification.action_metadata &&
    typeof notification.action_metadata === 'object'
  ) {
    navigate('/notifications', { state: notification.action_metadata })
    return true
  }
  return false
}
